// TimeAndMaterialView2.jsx
// Details tab: cost summary of all entries
// Entries tab: TandMEntriesPanel (manages all entries including Entry 1)
// Main "Download PDF" button: merges all non-rejected entries into one PDF

import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { DocumentArrowDownIcon } from "@heroicons/react/20/solid";
import TandMEntriesPanel from "./TandMEntriesPanel";
import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import AlertDialog from "./AlertDialog";
import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";
import { saveAs } from "file-saver";
import ImageModule from "docxtemplater-image-module-free";

const API_BASE = "https://erp-production-72da01c8e651.herokuapp.com/api";

const fmt = (num) =>
  Number.isFinite(num)
    ? `$${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : "$0.00";

const sanitizeForDocx = (str) => {
  if (!str) return "";
  return str
    .replace(/[\u2028\u2029]/g, " ")
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, "")
    .replace(/[\uFFF9-\uFFFF]/g, "")
    .replace(/[^\t\n\r\x20-\uD7FF\uE000-\uFFFD]/gu, "")
    .trim();
};

const STATUS_PILL = {
  Pending: "bg-yellow-400/20 text-yellow-400",
  Approved: "bg-green-400/20 text-green-400",
  Rejected: "bg-red-400/20 text-red-400",
};

export default function TandMView2({
  selectedEntry,
  companyProfile,
  costCodeOptions,
  vendorOptions,
  bidAreaOptions: bidAreaProp,
  setViewMode,
  project,
  setTimeAndMaterials,
  setProject,
  setIsCreating,
  setParentIsEditing,
  setEditingId,
  assemblyOptions: assemblyProp,
  setAssemblyOptions,
}) {
  const [alert, setAlert] = useState({
    open: false,
    type: "",
    title: "",
    message: "",
  });
  const [activeTab, setActiveTab] = useState("details");
  const [currentTandM, setCurrentTandM] = useState(selectedEntry);
  const [netQuantities, setNetQuantities] = useState(new Map());
  const [budgetItems, setBudgetItems] = useState([]);
  const [databaseItems, setDatabaseItems] = useState([]);
  const [loadingDatabase, setLoadingDatabase] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);

  useEffect(() => {
    setCurrentTandM(selectedEntry);
  }, [selectedEntry]);

  const items = currentTandM?.items || [];
  const entries = currentTandM?.entries || [];

  // ── Totals ──────────────────────────────────────────────────────────────────
  const originalBudgetTotal = useMemo(
    () =>
      items
        .filter((i) => i.forBudget !== false)
        .reduce((s, i) => s + Math.abs(i.amount || i.totalCost || 0), 0),
    [items],
  );

  const entryTotals = useMemo(
    () =>
      entries.map((e) => ({
        id: e._id,
        budgetTotal: (e.items || [])
          .filter((i) => i.forBudget !== false)
          .reduce((s, i) => s + Math.abs(i.amount || i.totalCost || 0), 0),
      })),
    [entries],
  );

  // Sum all non-rejected entries (pending + approved both count toward the picture)
  const allActiveEntriesTotal = useMemo(
    () =>
      entries
        .filter((e) => e.status !== "Rejected")
        .reduce((s, e) => {
          const t = entryTotals.find((x) => x.id === e._id);
          return s + (t?.budgetTotal || 0);
        }, 0),
    [entries, entryTotals],
  );

  const combinedTotal = originalBudgetTotal + allActiveEntriesTotal;
  const pendingCount = [
    ...(currentTandM?.status === "Pending" ? [1] : []),
    ...entries.filter((e) => e.status === "Pending"),
  ].length;

  // ── Derived options ─────────────────────────────────────────────────────────
  const derivedAssemblyOptions = useMemo(() => {
    const u = new Set();
    items.forEach((i) => {
      if (i.conditionName) u.add(i.conditionName);
    });
    entries.forEach((e) =>
      (e.items || []).forEach((i) => {
        if (i.conditionName) u.add(i.conditionName);
      }),
    );
    budgetItems.forEach((i) => {
      if (i.conditionName) u.add(i.conditionName);
    });
    (assemblyProp || []).forEach((o) => {
      if (o.value) u.add(o.value);
    });
    return Array.from(u)
      .sort()
      .map((v) => ({ value: v, label: v }));
  }, [items, entries, budgetItems, assemblyProp]);

  const derivedBidAreaOptions = useMemo(() => {
    const u = new Set();
    items.forEach((i) => {
      if (i.bidArea) u.add(i.bidArea);
    });
    entries.forEach((e) =>
      (e.items || []).forEach((i) => {
        if (i.bidArea) u.add(i.bidArea);
      }),
    );
    budgetItems.forEach((i) => {
      if (i.bidArea) u.add(i.bidArea);
    });
    (bidAreaProp || []).forEach((o) => {
      if (o.value) u.add(o.value);
    });
    return Array.from(u)
      .sort()
      .map((v) => ({ value: v, label: v }));
  }, [items, entries, budgetItems, bidAreaProp]);

  // ── Fetch helpers ───────────────────────────────────────────────────────────
  const fetchNetQuantities = useCallback(async () => {
    if (netQuantities.size > 0) return;
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `${API_BASE}/net-assemblies/project-by-source/${project._id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { category: "all", includeClosed: "true", mode: "combined" },
        },
      );
      const {
        combined = [],
        originalItems = [],
        changeOrderItems = [],
      } = res.data;
      const all =
        combined.length > 0
          ? combined
          : [...originalItems, ...changeOrderItems];
      const map = new Map();
      const unified = [];
      all.forEach((n) => {
        const key = (
          n.isNewSpec
            ? n.lineItemId
            : n.key || n.originalLineItemId || n.lineItemId || n._id
        )?.toString();
        if (!key) return;
        const norm = {
          ...n,
          _id: n.lineItemId || n._id || key,
          itemCode: n.itemCode || n.materialCode || n.laborCode || "",
          itemDescription: n.itemDescription || n.costCodeDescription || "",
          requiredQty: n.netRequiredQty || n.requiredQty || 0,
          remainingQty: n.netRemaining || 0,
          budgetedQty: n.baseRequiredQty || n.requiredQty || 0,
          unitCost: n.price || n.cost || n.unitRate || 0,
          latestPrice: n.latestPrice || n.price || 0,
          coveragePerUnit: n.coveragePerUnit || 1,
          size: n.latestSize || n.size || "",
          uom: n.uom || "",
          category: n.category || "M",
          conditionName: n.conditionName || "",
          bidArea: n.bidArea || "",
          vendor: n.vendor?._id || n.vendor || null,
        };
        map.set(key, norm);
        unified.push(norm);
      });
      setNetQuantities(map);
      setBudgetItems(unified);
    } catch (err) {
      console.error("fetchNetQuantities:", err);
    }
  }, [project?._id, netQuantities.size]);

  const fetchDatabaseItems = useCallback(async () => {
    if (databaseItems.length > 0) return;
    setLoadingDatabase(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_BASE}/item-database?limit=5000`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDatabaseItems(res.data.items || []);
    } catch (err) {
      console.error("fetchDatabaseItems:", err);
    } finally {
      setLoadingDatabase(false);
    }
  }, [databaseItems.length]);

  const handleTandMChange = (updated) => {
    setCurrentTandM(updated);
    setTimeAndMaterials((prev) =>
      prev.map((t) => (t._id === updated._id ? updated : t)),
    );
  };

  // ── Combined PDF: all non-rejected entries merged ───────────────────────────
  const generateCombinedPDF = async () => {
    if (!companyProfile) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Company profile not set.",
      });
      return;
    }

    const entriesToInclude = [
      {
        label: "Entry 1",
        items: items,
        scopeOfWork: currentTandM?.scopeOfWork,
        signedBy: currentTandM?.signedBy,
        signatureUrl: currentTandM?.signature,
        images: currentTandM?.images || [],
        status: currentTandM?.status,
        date: currentTandM?.signedAt || currentTandM?.createdAt,
      },
      ...entries
        .filter((e) => e.status !== "Rejected")
        .sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0))
        .map((e) => ({
          label: e.name,
          items: e.items || [],
          scopeOfWork: e.scopeOfWork,
          signedBy: e.signedBy,
          signatureUrl: e.signature,
          images: e.images || [],
          status: e.status,
          date: e.signedAt || e.createdAt,
        })),
    ].filter((e) => e.items.some((i) => i.forCustomer !== false));

    if (entriesToInclude.length === 0) {
      setAlert({
        open: true,
        type: "warning",
        title: "Warning",
        message: "No customer-facing items to include in PDF.",
      });
      return;
    }

    setGeneratingPDF(true);
    try {
      const base64Regex =
        /^(?:data:)?image\/(png|jpg|jpeg|svg|svg\+xml);base64,/;
      const validBase64 =
        /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;

      const b64parse = (v) => {
        if (typeof v !== "string" || !base64Regex.test(v)) return null;
        const s = v.replace(base64Regex, "");
        if (!validBase64.test(s)) throw new Error("Invalid base64");
        const bin = window.atob(s);
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        return bytes.buffer;
      };

      const fetchB64 = async (url) => {
        try {
          const r = await fetch(url, { mode: "cors" });
          if (!r.ok) return null;
          const blob = await r.blob();
          return new Promise((res) => {
            const rd = new FileReader();
            rd.onloadend = () => res(rd.result);
            rd.readAsDataURL(blob);
          });
        } catch {
          return null;
        }
      };

      const logoB64 = companyProfile?.logo
        ? await fetchB64(companyProfile.logo)
        : null;
      const taxRate = parseFloat(companyProfile?.defaultTax?.taxRate) || 0;
      const token = localStorage.getItem("token");

      // Generate one PDF blob per entry (same as CO approach)
      const generateSingleEntryBlob = async (entry) => {
        const pdfItems = (entry.items || [])
          .filter((i) => i.forCustomer !== false)
          .map((item, idx) => {
            const uom = item.uom || "EA";
            const isCov =
              item.category === "M" &&
              (uom.toLowerCase() === "sf" ||
                uom.toLowerCase().includes("square") ||
                uom.toLowerCase().includes("sq ft") ||
                uom.toLowerCase() === "sheet" ||
                uom.toLowerCase() === "lf" ||
                uom.toLowerCase().includes("linear") ||
                uom.toLowerCase().includes("lin ft"));
            const coverage = isCov ? parseFloat(item.coveragePerUnit) || 1 : 1;
            const amount = Math.abs(item.amount || item.totalCost || 0);
            return {
              itemNo: idx + 1,
              description:
                item.customerDescription ||
                item.title ||
                item.itemDescription ||
                "Unnamed",
              unit: isCov ? "PC" : uom,
              quantity: item.quantity || item.deltaQty || 0,
              price: ((item.unitCost || 0) * coverage).toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              }),
              amount: amount.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              }),
            };
          });

        if (pdfItems.length === 0) return null;

        const subtotal = pdfItems.reduce(
          (s, i) => s + (parseFloat(i.amount.replace(/[^0-9.-]+/g, "")) || 0),
          0,
        );
        const taxAmt = subtotal * (taxRate / 100);

        const sigB64 = entry.signatureUrl
          ? await fetchB64(entry.signatureUrl)
          : null;
        const imgs = [];
        for (const url of entry.images || []) {
          const b64 = await fetchB64(url);
          if (b64) imgs.push({ image: b64 });
        }

        const templateRes = await fetch("/t_and_m_template.docx");
        if (!templateRes.ok) throw new Error("Failed to load T&M template");
        const zip = new PizZip(await templateRes.arrayBuffer());
        const doc = new Docxtemplater(zip, {
          paragraphLoop: true,
          linebreaks: true,
          modules: [
            new ImageModule({
              getImage: b64parse,
              getSize: (img, val, name) =>
                name === "companyLogo"
                  ? [150, 100]
                  : name === "signature"
                    ? [100, 150]
                    : [200, 200],
            }),
          ],
        });

        doc.setData({
          projectName: project?.projectName || "Unknown Project",
          date: entry.date
            ? new Date(entry.date).toLocaleDateString()
            : new Date().toLocaleDateString(),
          preparedBy: currentTandM?.createdBy?.displayName || "Unknown",
          timeAndMaterialNumber: `${currentTandM?.timeAndMaterialNumber || "Draft"} – ${entry.label}`,
          scopeOfWork: sanitizeForDocx(entry.scopeOfWork || "N/A"),
          signedBy: entry.signedBy || "N/A",
          companyName: companyProfile?.companyName || "",
          companyAddress: companyProfile?.address
            ? `${companyProfile.address.street}, ${companyProfile.address.city}, ${companyProfile.address.state} ${companyProfile.address.zip}, ${companyProfile.address.country}`
            : "N/A",
          companyLogo: logoB64 || null,
          signature: sigB64 || null,
          items: pdfItems,
          pageBreak: "\f",
          hasImages: imgs.length > 0,
          images: imgs,
          subtotalPrice: subtotal.toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
          }),
          taxRate: taxRate.toFixed(2),
          taxAmount: taxAmt.toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
          }),
          totalPrice: (subtotal + taxAmt).toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
          }),
          overheadPercentage: (currentTandM?.overheadPercentage || 0).toFixed(
            2,
          ),
        });
        doc.render();

        const docxBlob = doc.getZip().generate({
          type: "blob",
          mimeType:
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        });

        const fd = new FormData();
        fd.append(
          "docx",
          docxBlob,
          `TandM_${currentTandM?._id}_${entry.label}.docx`,
        );
        fd.append("documentType", "TandM");
        fd.append("identifier", `${currentTandM?._id}_${entry.label}`);

        const pdfRes = await axios.post(
          `${API_BASE}/upload/convert-to-pdf`,
          fd,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
            responseType: "blob",
          },
        );

        if (pdfRes.status !== 200)
          throw new Error("T&M entry PDF conversion failed");

        let entryBlob = new Blob([pdfRes.data], { type: "application/pdf" });

        // Strip blank images page if no images
        if (imgs.length === 0) {
          const { PDFDocument } = await import("pdf-lib");
          const arrayBuffer = await entryBlob.arrayBuffer();
          const pdfDoc = await PDFDocument.load(arrayBuffer);
          const pageCount = pdfDoc.getPageCount();
          if (pageCount > 1) pdfDoc.removePage(pageCount - 1);
          const trimmedBytes = await pdfDoc.save();
          entryBlob = new Blob([trimmedBytes], { type: "application/pdf" });
        }

        return entryBlob;
      };

      // Generate all entry PDFs sequentially to preserve order
      const entryBlobs = [];
      for (const entry of entriesToInclude) {
        const blob = await generateSingleEntryBlob(entry);
        if (blob) entryBlobs.push(blob);
      }

      if (entryBlobs.length === 0) {
        setAlert({
          open: true,
          type: "warning",
          title: "Warning",
          message: "No pages generated.",
        });
        return;
      }

      // Merge all entry PDFs using pdf-lib
      const { PDFDocument } = await import("pdf-lib");
      const merged = await PDFDocument.create();
      for (const blob of entryBlobs) {
        const arrayBuffer = await blob.arrayBuffer();
        const pdf = await PDFDocument.load(arrayBuffer);
        const pages = await merged.copyPages(pdf, pdf.getPageIndices());
        pages.forEach((page) => merged.addPage(page));
      }
      const mergedBytes = await merged.save();
      const finalBlob = new Blob([mergedBytes], { type: "application/pdf" });

      saveAs(
        finalBlob,
        `TandM_${currentTandM?.timeAndMaterialNumber || "draft"}_all-entries.pdf`,
      );
      setAlert({
        open: true,
        type: "success",
        title: "Success",
        message: "Combined PDF downloaded.",
      });
    } catch (err) {
      console.error("Combined PDF error:", err);
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to generate PDF: " + err.message,
      });
    } finally {
      setGeneratingPDF(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="bg-gray-700 rounded-lg shadow-md border border-gray-600 p-4">
      <AlertDialog
        open={alert.open}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        confirmText="OK"
        onClose={() =>
          setAlert({ open: false, type: "", title: "", message: "" })
        }
        onConfirm={() =>
          setAlert({ open: false, type: "", title: "", message: "" })
        }
      />

      {/* Top bar */}
      <div className="flex justify-between items-start mb-4">
        <div>
          <h2 className="text-xl font-semibold text-white tracking-tight">
            T&M #{currentTandM?.timeAndMaterialNumber || "—"}
          </h2>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span
              className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${STATUS_PILL[currentTandM?.status] || STATUS_PILL.Pending}`}
            >
              {currentTandM?.status || "N/A"}
            </span>
            <span className="text-xs text-gray-400">
              {1 + entries.length} entr{1 + entries.length === 1 ? "y" : "ies"}
            </span>
            {pendingCount > 0 && (
              <span className="px-1.5 py-0.5 text-[10px] bg-orange-500 text-white rounded-full">
                {pendingCount} pending
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <button
            onClick={() => setViewMode("list")}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500 transition-colors shadow-sm"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1.5" /> Back
          </button>
          <button
            onClick={generateCombinedPDF}
            disabled={generatingPDF}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-200 rounded-md transition-colors shadow-sm ${generatingPDF ? "bg-gray-500 cursor-not-allowed" : "bg-orange-400 hover:bg-orange-500"}`}
          >
            <DocumentArrowDownIcon className="h-4 w-4" />
            {generatingPDF ? "Generating..." : "Download All PDF"}
          </button>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-gray-600 mb-4">
        <button
          onClick={() => setActiveTab("details")}
          className={`py-2 px-4 text-sm font-medium transition-colors rounded-t ${activeTab === "details" ? "text-orange-400 border-b-2 border-orange-400" : "text-gray-400 hover:text-gray-200"}`}
        >
          Summary
        </button>
        <button
          onClick={() => setActiveTab("entries")}
          className={`py-2 px-4 text-sm font-medium transition-colors rounded-t flex items-center gap-2 ${activeTab === "entries" ? "text-orange-400 border-b-2 border-orange-400" : "text-gray-400 hover:text-gray-200"}`}
        >
          Entries
          <span className="px-1.5 py-0.5 text-[10px] bg-gray-600 text-gray-300 rounded-full">
            {1 + entries.length}
          </span>
        </button>
      </div>

      {/* ── SUMMARY TAB ── */}
      {activeTab === "details" && (
        <div className="space-y-5">
          {/* Meta */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <h4 className="text-xs font-medium text-gray-400 uppercase mb-0.5">
                Created By
              </h4>
              <p className="text-sm text-gray-200">
                {currentTandM?.createdBy?.displayName || "Unknown"}
              </p>
            </div>
            <div>
              <h4 className="text-xs font-medium text-gray-400 uppercase mb-0.5">
                Date
              </h4>
              <p className="text-sm text-gray-200">
                {currentTandM?.signedAt
                  ? new Date(currentTandM.signedAt).toLocaleDateString()
                  : "N/A"}
              </p>
            </div>
            <div>
              <h4 className="text-xs font-medium text-gray-400 uppercase mb-0.5">
                Project
              </h4>
              <p className="text-sm text-gray-200">
                {project?.projectName || "—"}
              </p>
            </div>
            <div>
              <h4 className="text-xs font-medium text-gray-400 uppercase mb-0.5">
                Total Entries
              </h4>
              <p className="text-sm text-gray-200">{1 + entries.length}</p>
            </div>
          </div>

          {/* Cost summary table */}
          <div className="bg-gray-800 rounded-lg border border-gray-600 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-700">
              <h3 className="text-sm font-semibold text-white">Cost Summary</h3>
            </div>
            <div className="divide-y divide-gray-700/50">
              {/* Entry 1 */}
              <div className="px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className={`w-2 h-2 rounded-full flex-shrink-0 ${currentTandM?.status === "Approved" ? "bg-green-400" : currentTandM?.status === "Rejected" ? "bg-red-400" : "bg-yellow-400"}`}
                  />
                  <span className="text-sm text-gray-200 font-medium">
                    Entry 1 — Original
                  </span>
                  <span
                    className={`px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${STATUS_PILL[currentTandM?.status] || STATUS_PILL.Pending}`}
                  >
                    {currentTandM?.status}
                  </span>
                  <span className="text-xs text-gray-500">
                    {items.length} items
                  </span>
                  {currentTandM?.signedBy && (
                    <span className="text-xs text-gray-500">
                      · {currentTandM.signedBy}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`text-sm font-semibold ${currentTandM?.status === "Approved" ? "text-blue-400" : "text-gray-300"}`}
                  >
                    {fmt(originalBudgetTotal)}
                  </span>
                  {currentTandM?.status === "Pending" && (
                    <span className="text-xs text-gray-600 italic">
                      pending
                    </span>
                  )}
                </div>
              </div>

              {/* Additional entries */}
              {entries.map((entry) => {
                const t = entryTotals.find((x) => x.id === entry._id) || {};
                return (
                  <div
                    key={entry._id}
                    className="px-4 py-3 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`w-2 h-2 rounded-full flex-shrink-0 ${entry.status === "Approved" ? "bg-green-400" : entry.status === "Rejected" ? "bg-red-400" : "bg-yellow-400"}`}
                      />
                      <span className="text-sm text-gray-300 font-medium">
                        {entry.name}
                      </span>
                      <span
                        className={`px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${STATUS_PILL[entry.status] || STATUS_PILL.Pending}`}
                      >
                        {entry.status}
                      </span>
                      <span className="text-xs text-gray-500">
                        {(entry.items || []).length} items
                      </span>
                      {entry.signedBy && (
                        <span className="text-xs text-gray-500">
                          · {entry.signedBy}
                        </span>
                      )}
                      {entry.createdAt && (
                        <span className="text-xs text-gray-500">
                          · {new Date(entry.createdAt).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-sm font-semibold ${entry.status === "Approved" ? "text-green-400" : entry.status === "Rejected" ? "text-gray-600 line-through" : "text-gray-300"}`}
                      >
                        {fmt(t.budgetTotal || 0)}
                      </span>
                      {entry.status === "Pending" && (
                        <span className="text-xs text-gray-600 italic">
                          pending
                        </span>
                      )}
                      {entry.status === "Rejected" && (
                        <span className="text-xs text-red-500/60 italic">
                          rejected
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Grand total */}
              <div className="px-4 py-4 bg-gray-700/30 flex items-center justify-between">
                <div>
                  <span className="text-sm font-bold text-white">
                    Combined Total
                  </span>
                  <div className="text-xs text-gray-500 mt-0.5">
                    All pending + approved (rejected excluded)
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xl font-bold text-orange-400">
                    {fmt(combinedTotal)}
                  </div>
                  {allActiveEntriesTotal > 0 && originalBudgetTotal > 0 && (
                    <div className="text-xs text-gray-500 mt-0.5">
                      Original {fmt(originalBudgetTotal)} + Entries{" "}
                      {fmt(allActiveEntriesTotal)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Quick tip */}
          <div className="text-xs text-gray-500 text-center">
            Go to the{" "}
            <button
              type="button"
              onClick={() => setActiveTab("entries")}
              className="text-orange-400 hover:underline"
            >
              Entries tab
            </button>{" "}
            to view details, edit, approve, reject, or download individual PDFs.
          </div>
        </div>
      )}

      {/* ── ENTRIES TAB ── */}
      {activeTab === "entries" && (
        <TandMEntriesPanel
          tandm={currentTandM}
          projectId={project._id}
          token={localStorage.getItem("token")}
          showAlert={(type, title, message) =>
            setAlert({ open: true, type, title, message })
          }
          onTandMChange={handleTandMChange}
          budgetItems={budgetItems}
          databaseItems={databaseItems}
          loadingDatabase={loadingDatabase}
          netQuantities={netQuantities}
          fetchNetQuantities={fetchNetQuantities}
          fetchDatabaseItems={fetchDatabaseItems}
          companyProfile={companyProfile}
          project={project}
          assemblyOptions={derivedAssemblyOptions}
          bidAreaOptions={derivedBidAreaOptions}
        />
      )}
    </div>
  );
}
